/**
 * ProfileDialog Component
 *
 * Dialog for viewing and editing the current user's profile,
 * including password change functionality.
 */

import { useState, useEffect } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Grid,
  Typography,
  Divider,
  Alert,
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  Radio,
  RadioGroup,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { UserAvatar } from '@/components/common';
import { MobileLoginCodeSection } from './MobileLoginCodeSection';
import { StaffWhatsAppVerificationSection } from './StaffWhatsAppVerificationSection';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import i18n from '@/lib/i18n';
import { useAuthStore } from '@/stores/authStore';
import { useUpdateProfile, useChangePassword } from '@/hooks/useProfile';
import {
  isAssistantMode,
  useAssistantPreferences,
  useUpdateAssistantPreferences,
  type AssistantMode,
} from '@/hooks/useAssistantPreferences';
import { useAuthContext, useCurrentUser } from '@/hooks/useAuth';
import { useNotify } from '@/hooks/useNotify';
import { api, type ApiError } from '@/lib/api';

interface ProfileDialogProps {
  open: boolean;
  onClose: () => void;
  forced?: boolean;
  onForcedPasswordChanged?: () => void;
}

interface ProfileFormData {
  firstName: string;
  lastName: string;
  phone: string;
  preferredLanguage: string;
}

interface PasswordFormData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface UserProfileResponse {
  id: string;
  phone: string | null;
  preferredLanguage: string;
}

const initialPasswordData: PasswordFormData = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

/** Mode copy in the settings namespace. Keys stay out of the JSX text. */
const BUDDY_MODE_COPY = {
  ask: { labelKey: 'buddy.ask', hintKey: 'buddy.askHint' },
  guide: { labelKey: 'buddy.guide', hintKey: 'buddy.guideHint' },
  do: { labelKey: 'buddy.do', hintKey: 'buddy.doHint' },
} as const satisfies Record<AssistantMode, { labelKey: string; hintKey: string }>;

const BUDDY_MODES = Object.keys(BUDDY_MODE_COPY) as AssistantMode[];

export function ProfileDialog({ open, onClose, forced = false, onForcedPasswordChanged }: ProfileDialogProps) {
  const { t } = useTranslation();
  const { t: tc } = useTranslation('common');
  const { t: tSettings } = useTranslation('settings');
  const notify = useNotify();
  const user = useAuthStore((s) => s.user);

  const updateProfileMutation = useUpdateProfile();
  const changePasswordMutation = useChangePassword();
  const assistantPreferences = useAssistantPreferences(open && !forced);
  const updateAssistantPreferences = useUpdateAssistantPreferences();

  // Read from the query, not the auth store: the store is hydrated from
  // localStorage and only re-synced when the role, tenant or permissions
  // change, so a field added later would stay missing for everyone already
  // signed in. `/auth/me` always carries it.
  const { data: currentUser } = useCurrentUser();
  const { data: authContext } = useAuthContext();

  // Requires a relying party to hand the change to. Without one the password
  // form is what works, and hiding it would leave a user who happens to carry a
  // principal with no way to change anything.
  const payaIdPasswordOnly =
    !forced && (currentUser?.linkedToPayaId ?? false) && (authContext?.oidcEnabled ?? false);

  /**
   * Change the Paya ID password without leaving OS Buddy's world (#1794).
   *
   * Whole-window navigation into the ordinary sign-in flow, which the server
   * turns into a Keycloak application initiated action. The password page is
   * the **login** theme — OS Buddy's own — and the browser comes straight back
   * here. The account console this used to link to is the identity server's own
   * product and has nothing in it for a workshop user.
   */
  const startPasswordChange = (): void => {
    const back = window.location.pathname + window.location.search;
    window.location.assign(
      `/api/v1/auth/oidc/start?intent=update-password&return=${encodeURIComponent(back)}`,
    );
  };

  const [profileData, setProfileData] = useState<ProfileFormData>({
    firstName: '',
    lastName: '',
    phone: '',
    preferredLanguage: 'fi',
  });
  const [profileErrors, setProfileErrors] = useState<Partial<Record<keyof ProfileFormData, string>>>({});
  const [profileIsDirty, setProfileIsDirty] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  /** Unsaved Buddy mode. Null means the radios show the stored mode. */
  const [buddyDraft, setBuddyDraft] = useState<AssistantMode | null>(null);
  const [passwordData, setPasswordData] = useState<PasswordFormData>(initialPasswordData);
  const [passwordErrors, setPasswordErrors] = useState<Partial<Record<keyof PasswordFormData, string>>>({});
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  /**
   * A mobile login code is being minted (#1244 E6).
   *
   * Lives here rather than in the section because MUI unmounts a closed dialog's
   * children: closing mid-mint destroys that subtree, discarding the only copy of
   * a credential that is live for the next 90 seconds, and letting a reopened
   * instance start a second mint that can race the first. So every close path is
   * blocked for the sub-second the mint takes.
   */
  const [isMintingMobileCode, setIsMintingMobileCode] = useState(false);
  const isBusy = isMintingMobileCode;

  useEffect(() => {
    if (open && user) {
      setProfileData({
        firstName: user.firstName,
        lastName: user.lastName,
        phone: '',
        preferredLanguage: 'fi',
      });
      setProfileErrors({});
      setProfileIsDirty(false);
      setPasswordData(initialPasswordData);
      setPasswordErrors({});
      setPasswordSuccess(false);
    }
  }, [open, user]);

  // The stored mode is not part of the user row this effect resets. Clear only
  // an unsent choice when the dialog opens, so a profile save cannot drop it.
  useEffect(() => {
    if (open) setBuddyDraft(null);
  }, [open]);

  useEffect(() => {
    if (open && user) {
      void api
        .get<UserProfileResponse>(`/users/${user.id}`)
        .then((data) => {
          setProfileData((prev) => ({
            ...prev,
            phone: data.phone ?? '',
            preferredLanguage: data.preferredLanguage || 'fi',
          }));
        })
        .catch(() => {
          // Silently fail, form will use defaults
        });
    }
  }, [open, user]);

  const handleProfileChange = (field: keyof ProfileFormData, value: string) => {
    setProfileData((prev) => ({ ...prev, [field]: value }));
    setProfileIsDirty(true);
    if (profileErrors[field]) {
      setProfileErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handlePasswordChange = (field: keyof PasswordFormData, value: string) => {
    setPasswordData((prev) => ({ ...prev, [field]: value }));
    if (passwordErrors[field]) {
      setPasswordErrors((prev) => ({ ...prev, [field]: undefined }));
    }
    setPasswordSuccess(false);
  };

  const validateProfile = (): boolean => {
    const errors: Partial<Record<keyof ProfileFormData, string>> = {};

    if (!profileData.firstName.trim()) {
      errors.firstName = t('validation.required');
    }
    if (!profileData.lastName.trim()) {
      errors.lastName = t('validation.required');
    }

    setProfileErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validatePassword = (): boolean => {
    const errors: Partial<Record<keyof PasswordFormData, string>> = {};

    if (!passwordData.currentPassword) {
      errors.currentPassword = t('validation.required');
    }
    if (!passwordData.newPassword) {
      errors.newPassword = t('validation.required');
    } else if (passwordData.newPassword.length < 8) {
      errors.newPassword = t('validation.minLength', { min: 8 });
    }
    if (!passwordData.confirmPassword) {
      errors.confirmPassword = t('validation.required');
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = t('profile.passwordMismatch');
    }

    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveProfile = async () => {
    if (!validateProfile()) return;

    try {
      await updateProfileMutation.mutateAsync({
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        phone: profileData.phone || null,
        preferredLanguage: profileData.preferredLanguage,
      });
      if (profileData.preferredLanguage !== i18n.language) {
        void i18n.changeLanguage(profileData.preferredLanguage);
      }
      setProfileIsDirty(false);
      notify.success(t('profile.profileUpdated'), t('profile.profileUpdatedDesc'));
    } catch {
      notify.error(t('error'), t('profile.profileUpdateError'));
    }
  };

  const storedAssistantMode = assistantPreferences.data?.assistantMode;
  const selectedAssistantMode = buddyDraft ?? storedAssistantMode;
  const buddyIsDirty = buddyDraft !== null && buddyDraft !== storedAssistantMode;

  const handleSaveBuddy = async () => {
    if (!selectedAssistantMode) return;
    try {
      await updateAssistantPreferences.mutateAsync(selectedAssistantMode);
      setBuddyDraft(null);
      notify.success(tSettings('buddy.saved'), tSettings('buddy.savedDesc'));
    } catch {
      notify.error(t('error'), tSettings('buddy.saveError'));
    }
  };

  const handleChangePassword = async () => {
    if (!validatePassword()) return;

    try {
      await changePasswordMutation.mutateAsync({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });
      setPasswordData(initialPasswordData);
      setPasswordErrors({});
      setPasswordSuccess(true);

      if (forced) {
        const currentUser = useAuthStore.getState().user;
        if (currentUser) {
          useAuthStore.getState().setUser({ ...currentUser, forcePasswordChange: false });
        }
        onForcedPasswordChanged?.();
      } else {
        notify.success(t('profile.passwordChanged'), t('profile.passwordChangedDesc'));
      }
    } catch (error) {
      const apiError = error as ApiError;
      if (apiError.status === 401) {
        setPasswordErrors({ currentPassword: t('profile.wrongPassword') });
      } else {
        notify.error(t('error'), t('profile.passwordChangeError'));
      }
    }
  };

  const handleClose = () => {
    // A mint in flight holds the only copy of a live credential; let it land.
    if (isBusy) return;
    if (profileIsDirty || buddyIsDirty) {
      setShowDiscardConfirm(true);
    } else {
      onClose();
    }
  };

  if (!user) return null;

  const isProfilePending = updateProfileMutation.isPending;
  const isPasswordPending = changePasswordMutation.isPending;
  const isBuddyPending = updateAssistantPreferences.isPending;

  return (
    <>
      <ResponsiveDialog
        mobileTitle={t('profile.title')}
        open={open}
        onClose={forced || isBusy ? undefined : handleClose}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={forced || isBusy}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {forced ? t('profile.forceChangeTitle') : t('profile.title')}
          {!forced && (
            <Tooltip title={tc('close')}>
              <span>
                <IconButton
                  onClick={handleClose}
                  size="small"
                  aria-label={tc('close')}
                  disabled={isBusy}
                >
                  <CloseIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={3}>
            {forced ? (
              <Alert severity="info">
                {t('profile.forceChangeMessage')}
              </Alert>
            ) : (
              <>
                {/* User info header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <UserAvatar
                    firstName={user.firstName}
                    lastName={user.lastName}
                    avatarUrl={user.avatarUrl}
                    size={64}
                  />
                  <Box>
                    <Typography variant="h6">
                      {user.firstName} {user.lastName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {user.email}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {tSettings(`roles.${user.role === 'service_advisor' ? 'receptionist' : user.role}`)}
                    </Typography>
                  </Box>
                </Box>

                <Divider />

                {/* Profile info section */}
                <Box component="form" onSubmit={(e: React.FormEvent) => { e.preventDefault(); void handleSaveProfile(); }}>
                  <Stack spacing={3}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {t('profile.personalInfo')}
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          label={t('profile.firstName')}
                          value={profileData.firstName}
                          onChange={(e) => { handleProfileChange('firstName', e.target.value); }}
                          error={!!profileErrors.firstName}
                          helperText={profileErrors.firstName}
                          fullWidth
                          required
                          autoFocus
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          label={t('profile.lastName')}
                          value={profileData.lastName}
                          onChange={(e) => { handleProfileChange('lastName', e.target.value); }}
                          error={!!profileErrors.lastName}
                          helperText={profileErrors.lastName}
                          fullWidth
                          required
                        />
                      </Grid>
                    </Grid>

                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          label={t('profile.phone')}
                          value={profileData.phone}
                          onChange={(e) => { handleProfileChange('phone', e.target.value); }}
                          fullWidth
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControl fullWidth>
                          <InputLabel>{t('language')}</InputLabel>
                          <Select
                            value={profileData.preferredLanguage}
                            label={t('language')}
                            onChange={(e) => { handleProfileChange('preferredLanguage', e.target.value); }}
                          >
                            <MenuItem value="fi">Suomi</MenuItem>
                            <MenuItem value="en">English</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={isProfilePending}
                      >
                        {isProfilePending ? <CircularProgress size={20} /> : t('save')}
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                <Divider />

                <Box
                  component="form"
                  aria-label={tSettings('buddy.title')}
                  onSubmit={(e: React.FormEvent) => { e.preventDefault(); void handleSaveBuddy(); }}
                >
                  <Stack spacing={2}>
                    <Typography id="profile-buddy-heading" variant="subtitle1" fontWeight={600}>
                      {tSettings('buddy.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {tSettings('buddy.description')}
                    </Typography>

                    {assistantPreferences.isPending ? (
                      <CircularProgress size={20} aria-label={t('loading')} />
                    ) : assistantPreferences.isError || !selectedAssistantMode ? (
                      <Alert severity="error">{tSettings('buddy.loadError')}</Alert>
                    ) : (
                      <>
                        <RadioGroup
                          aria-labelledby="profile-buddy-heading"
                          name="assistantMode"
                          value={selectedAssistantMode}
                          onChange={(event) => {
                            if (!isAssistantMode(event.target.value)) return;
                            const next = event.target.value;
                            setBuddyDraft(next === storedAssistantMode ? null : next);
                          }}
                        >
                          {BUDDY_MODES.map((mode) => (
                            <FormControlLabel
                              key={mode}
                              value={mode}
                              control={<Radio size="small" />}
                              sx={{ alignItems: 'flex-start' }}
                              label={
                                <Box sx={{ pt: 0.75 }}>
                                  <Typography variant="body2">{tSettings(BUDDY_MODE_COPY[mode].labelKey)}</Typography>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    {tSettings(BUDDY_MODE_COPY[mode].hintKey)}
                                  </Typography>
                                </Box>
                              }
                            />
                          ))}
                        </RadioGroup>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Button
                            type="submit"
                            variant="contained"
                            disabled={isBuddyPending || !buddyIsDirty}
                          >
                            {isBuddyPending ? <CircularProgress size={20} /> : t('save')}
                          </Button>
                        </Box>
                      </>
                    )}
                  </Stack>
                </Box>

                <Divider />
              </>
            )}

            {/*
              Password change section.
              Replaced by a single button that hands the change to Paya ID for
              accounts that sign in with it (#1741): this form writes
              `users.password_hash`, which a Paya ID sign-in never reads. The
              button says only "change password" because where the change happens
              is the product's problem, not the user's. For a migrated row the
              form changes a credential the user does not use; for an invited row
              there is no hash at all and the route 400s behind a toast that
              explains nothing.
              Still shown while `forced` — that path is reached only through the
              password login, where the password is the credential in use, and
              hiding the form there would strand the user with no way to clear
              the flag.
            */}
            {payaIdPasswordOnly ? (
              <Box>
                <Button variant="outlined" onClick={startPasswordChange}>
                  {t('profile.changePassword')}
                </Button>
              </Box>
            ) : (
            <Box component="form" onSubmit={(e: React.FormEvent) => { e.preventDefault(); void handleChangePassword(); }}>
              <Stack spacing={3}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {t('profile.changePassword')}
                </Typography>

                {passwordSuccess && (
                  <Alert severity="success">
                    {t('profile.passwordChangedDesc')}
                  </Alert>
                )}

                <TextField
                  label={t('profile.currentPassword')}
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => { handlePasswordChange('currentPassword', e.target.value); }}
                  error={!!passwordErrors.currentPassword}
                  helperText={passwordErrors.currentPassword}
                  fullWidth
                  required
                />

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label={t('profile.newPassword')}
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) => { handlePasswordChange('newPassword', e.target.value); }}
                      error={!!passwordErrors.newPassword}
                      helperText={passwordErrors.newPassword ?? t('profile.passwordHint')}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label={t('profile.confirmPassword')}
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => { handlePasswordChange('confirmPassword', e.target.value); }}
                      error={!!passwordErrors.confirmPassword}
                      helperText={passwordErrors.confirmPassword}
                      fullWidth
                      required
                    />
                  </Grid>
                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isPasswordPending}
                  >
                    {isPasswordPending ? <CircularProgress size={20} /> : t('profile.changePassword')}
                  </Button>
                </Box>
              </Stack>
            </Box>
            )}

            {/*
              The mobile login QR (#1244 E6, G6). Hidden while `forced`: that
              variant exists solely to make the user change an expired password and
              deliberately shows nothing else, and a user who has not finished it
              should not be minting a session for another device.
            */}
            {!forced && (
              <>
                <Divider />
                {/* Own-profile verification of the staff WhatsApp number (#3874). */}
                <StaffWhatsAppVerificationSection />
                <Divider />
                <MobileLoginCodeSection
                  email={user.email}
                  // Same signal as the password section, different consequence:
                  // there the form is useless, here it is the only way in and
                  // there is nothing to type (#1763).
                  viaPayaId={
                    (currentUser?.linkedToPayaId ?? false) && (authContext?.oidcEnabled ?? false)
                  }
                  onBusyChange={setIsMintingMobileCode}
                />
              </>
            )}
          </Stack>
        </DialogContent>

        {!forced && (
          <DialogActions>
            <Button onClick={handleClose} disabled={isBusy}>{t('close')}</Button>
          </DialogActions>
        )}
      </ResponsiveDialog>

      <ConfirmDialog
        open={showDiscardConfirm}
        title={tc('unsavedChanges')}
        message={tc('unsavedChangesMessage')}
        confirmText={tc('discard')}
        confirmColor="error"
        onConfirm={() => {
          setShowDiscardConfirm(false);
          setProfileIsDirty(false);
          setBuddyDraft(null);
          onClose();
        }}
        onCancel={() => setShowDiscardConfirm(false)}
      />
    </>
  );
}
